import ApiManager from '@/managers/apimanagers';
import dayjs from 'dayjs';
import { inject } from 'vue';
import { useMessage } from '@/const/messages';
export { watchNetwork, languageToCode, setTimezone, decimalize, decimalizeByStep, decimalizeByAny, decimalStep, decimalizeByExchange, };
const watchNetwork = () => {
    let networkCount = 0;
    const { format, EXCEPTIONS } = useMessage();
    const toast = inject('toast');
    setInterval(() => {
        ApiManager.get('/health')
            .then(() => {
            networkCount = 0;
        })
            .catch((err) => {
            if (err.message.match(/status code 503/)) {
                networkCount = 0;
                return;
            }
            if (networkCount > 0) {
                toast.warning(format(EXCEPTIONS.NETWORK_ERROR), {
                    position: 'top-right',
                    duration: process.env.VUE_APP_DISCONNECT_ALERT_SEC * 1000,
                    onClick: () => location.reload(),
                });
            }
            networkCount += 1;
        });
    }, process.env.VUE_APP_DISCONNECT_ALERT_SEC * 1000);
};
const languageToCode = (language) => {
    if (language === 'English') {
        return 'en';
    }
    if (language === 'Japanese') {
        return 'ja';
    }
    if (language === 'Traditional' || language === 'Simplified') {
        return 'zh';
    }
    return 'en';
};
/**
 * @summary タイムゾーンに応じた時間調整
 * @param timezone タイムゾーン文字列 UTC Asia/Tokyoなど
 * @param time フォーマット対象の日付
 * @param format 整形の形式 YYYY-MM-DDなど
 */
const setTimezone = (timezone, time, format) => {
    if (timezone === 'Asia/Tokyo') {
        return dayjs(time).add(9, 'h').format(format);
    }
    if (timezone === 'Asia/Shanghai' || timezone === 'Asia/Singapore') {
        return dayjs(time).add(8, 'h').format(format);
    }
    if (timezone === 'Asia/Dubai') {
        return dayjs(time).add(4, 'h').format(format);
    }
    if (timezone === 'America/New_York') {
        return dayjs(time).add(-5, 'h').format(format);
    }
    return dayjs(time).format(format);
};
/**
 * @summary 通貨によって桁数を調整し、カンマ付与処理を行う
 * @param target 処理対象の数値
 * @param symbol 処理対象の通貨
 * @param currencyInfo 通貨情報一覧
 */
const decimalize = (target, symbol, currencyInfo, isLeaveZero = false) => {
    const currency = currencyInfo.find((c) => c.symbol === symbol);
    if (!currency) {
        return target;
    }
    if (isLeaveZero) {
        return Number(target).toLocaleString();
    }
    const decimal = Number(currency.decimal);
    return Number(target).toLocaleString(undefined, {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
    });
};
/**
 * @summary 通貨ペアによって桁数を調整し、カンマ付与処理を行う
 * @param target 処理対象の数値
 * @param pair 処理対象の通貨ペア
 * @param currencyPairInfo 通貨ペア情報一覧
 */
const decimalizeByExchange = (target, pair, currencyPairInfo) => {
    const currencyPair = currencyPairInfo.find((c) => c.pair === pair);
    if (!currencyPair) {
        return target;
    }
    const decimal = Number(currencyPair.decimal);
    return Number(target).toLocaleString(undefined, {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
    });
};
/**
 * @summary 6桁または通貨の桁数のうち小さい方の桁数に調整し、カンマ付与処理を行う
 * @param symbol 処理対象
 * @param currencyInfo 通貨ペア情報一覧
 */
const decimalizeByAny = (target, symbol, currencyInfo, limitDecimal = 6) => {
    const currency = currencyInfo.find((c) => c.symbol === symbol);
    if (!currency) {
        return target;
    }
    const decimal = Math.min(currency.decimal, limitDecimal);
    return Number(target).toLocaleString(undefined, {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
    });
};
/**
 * @summary 通貨の桁数に調整し、カンマ付与処理を行う
 * @param symbol 処理対象の数値
 * @param currencyInfo 通貨ペア情報一覧
 */
const decimalizeByStep = (target, symbol, currencyInfo) => {
    const currency = currencyInfo.find((c) => c.symbol === symbol);
    if (!currency) {
        return target;
    }
    return Number(target).toLocaleString(undefined, {
        minimumFractionDigits: currency.decimal,
        maximumFractionDigits: currency.decimal,
    });
};
/**
 * @summary 通貨の桁数の最小単位を返す(0.000001)など
 * @param symbol 対象の通貨
 */
const decimalStep = (symbol, currencyInfo) => {
    const currency = currencyInfo.find((c) => c.symbol === symbol);
    if (!currency) {
        return 1;
    }
    const decimal = Number(currency.decimal);
    return Math.pow(10, -decimal);
};
/**
 * @summary 通貨ペアの有効桁数の最小単位を返す(0.000001)など
 * @param pair 対象の通貨ペア
 */
export const priceDecimalStep = (pair, currencyPairInfo) => {
    const currency = currencyPairInfo.find((c) => c.pair === pair);
    if (!currency) {
        return String(1);
    }
    const decimal = Number(currency.decimal);
    return minimumDigits(decimal);
};
/**
 * @summary 通貨ペアの数量のステップ数を返す
 * @param symbol 対象の通貨ペア
 */
export const amountDecimalStep = (symbol, currencyInfo) => {
    const currency = currencyInfo.find((c) => c.symbol === symbol);
    if (!currency) {
        return String(1);
    }
    return minimumDigits(currency.decimal);
};
/**
 * 引数の桁数を基に、最小値を取得します
 *
 * @param decimal 小数点以下の桁数
 * @returns
 */
const minimumDigits = (decimal) => {
    return [...Array(decimal - 1)].reduce((i) => (i += '0'), '0.') + '1';
};
